/**
 * Snapshot Comparison Utility
 *
 * Compares two data snapshots to detect new, changed, and removed model offerings.
 */

import {
  DataSnapshot,
  ModelOffering,
  SnapshotComparison,
  ProfileChange,
  ChangedFieldType,
} from '@bedrock-profiles/shared';
import { getModelOfferings } from './modelOfferings';

/**
 * Checks if two arrays are equal (order-independent)
 */
function arraysEqual(arr1: string[] | null | undefined, arr2: string[] | null | undefined): boolean {
  // Handle null/undefined cases
  if (!arr1 && !arr2) return true;
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return sorted1.every((val, index) => val === sorted2[index]);
}

/**
 * Compares two offerings and returns which fields changed
 */
function detectChangedFields(
  current: ModelOffering,
  previous: ModelOffering
): ChangedFieldType[] {
  const changedFields: ChangedFieldType[] = [];

  // Check destination regions (order-independent array comparison, handle nullable)
  if (!arraysEqual(current.deployment.destinationRegions, previous.deployment.destinationRegions)) {
    changedFields.push('destinationRegions');
  }

  // Check model lifecycle status
  if (current.modelLifecycleStatus !== previous.modelLifecycleStatus) {
    changedFields.push('modelLifecycleStatus');
  }

  // Check description
  if (current.description !== previous.description) {
    changedFields.push('description');
  }

  return changedFields;
}

/**
 * Compares two data snapshots to detect changes
 *
 * @param current - Current snapshot
 * @param previous - Previous snapshot (optional)
 * @returns Comparison result with change information
 */
export function compareSnapshots(
  current: DataSnapshot,
  previous?: DataSnapshot
): SnapshotComparison {
  const changes = new Map<string, ProfileChange>();
  let newCount = 0;
  let changedCount = 0;
  let removedCount = 0;

  const currentOfferings = getModelOfferings(current);
  const previousOfferings = previous ? getModelOfferings(previous) : undefined;

  // If no previous snapshot exists, all current offerings are "new"
  if (!previous) {
    currentOfferings.forEach((offering) => {
      changes.set(offering.offeringId, {
        profileId: offering.offeringId,
        status: 'new',
      });
      newCount++;
    });

    return {
      currentVersion: current.version,
      previousVersion: undefined,
      changes,
      newCount,
      changedCount: 0,
      removedCount: 0,
    };
  }

  // Create a map of previous offerings for O(1) lookup
  // Index by both offeringId and legacyProfileId so comparisons work
  // across the ":0" suffix removal transition
  const previousOfferingsMap = new Map<string, ModelOffering>();
  previousOfferings?.forEach((offering) => {
    previousOfferingsMap.set(offering.offeringId, offering);
    if (offering.legacyProfileId && offering.legacyProfileId !== offering.offeringId) {
      previousOfferingsMap.set(offering.legacyProfileId, offering);
    }
  });

  // Check each offering in current snapshot
  currentOfferings.forEach((currentOffering) => {
    const previousOffering = previousOfferingsMap.get(currentOffering.offeringId)
      || (currentOffering.legacyProfileId
        ? previousOfferingsMap.get(currentOffering.legacyProfileId)
        : undefined);

    if (!previousOffering) {
      // Offering exists in current but not in previous -> NEW
      changes.set(currentOffering.offeringId, {
        profileId: currentOffering.offeringId,
        status: 'new',
      });
      newCount++;
    } else {
      // Offering exists in both -> Check for changes
      const changedFields = detectChangedFields(currentOffering, previousOffering);

      if (changedFields.length > 0) {
        changes.set(currentOffering.offeringId, {
          profileId: currentOffering.offeringId,
          status: 'changed',
          changedFields,
        });
        changedCount++;
      } else {
        changes.set(currentOffering.offeringId, {
          profileId: currentOffering.offeringId,
          status: 'unchanged',
        });
      }
    }
  });

  // Check for removed offerings (in previous but not in current)
  // Also check legacyProfileId to handle the ":0" suffix removal transition
  const currentOfferingIds = new Set(currentOfferings.map((o) => o.offeringId));
  const currentLegacyIds = new Set(
    currentOfferings.map((o) => o.legacyProfileId).filter(Boolean) as string[]
  );
  previousOfferings?.forEach((previousOffering) => {
    if (!currentOfferingIds.has(previousOffering.offeringId)
        && !currentLegacyIds.has(previousOffering.offeringId)) {
      changes.set(previousOffering.offeringId, {
        profileId: previousOffering.offeringId,
        status: 'removed',
      });
      removedCount++;
    }
  });

  return {
    currentVersion: current.version,
    previousVersion: previous.version,
    changes,
    newCount,
    changedCount,
    removedCount,
  };
}

/**
 * Helper to check if a profile has changed since previous snapshot
 */
export function hasProfileChanged(
  profileId: string,
  comparison?: SnapshotComparison
): boolean {
  if (!comparison) return false;
  const change = comparison.changes.get(profileId);
  return change?.status === 'changed' || change?.status === 'new';
}

/**
 * Helper to get change information for a profile
 */
export function getProfileChange(
  profileId: string,
  comparison?: SnapshotComparison
): ProfileChange | undefined {
  return comparison?.changes.get(profileId);
}
